import axios from 'axios';
import { refreshToken, isTokenExpired } from '@/lib/auth';
import { captureException } from '@/lib/sentry';
import { toast } from '@/hooks/use-toast';
import { CompatibilityStatus, CoreVersionInfoResponse, SearchResultsResponse } from '@/lib/api/types';
import { API_CONFIG } from '@/lib/config/api-config';

// Use dynamic API configuration that supports runtime switching
export const getApiBaseUrl = () => API_CONFIG.BASE_URL;

const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to track if we're currently refreshing the token
let isRefreshing = false;
// Store pending requests that should be retried after token refresh
let failedQueue = [];
let frontendCompatibilityStatus: CompatibilityStatus = 'unknown';

export const setFrontendCompatibilityStatus = (status: CompatibilityStatus) => {
  frontendCompatibilityStatus = status;
};

// Process the queue of failed requests
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Check if this is a login attempt
const isLoginAttempt = (config) => {
  return config.url?.includes('/token/pair') && config.method === 'post';
};

const isWriteMethod = (method) => ['post', 'put', 'patch', 'delete'].includes((method || '').toLowerCase());

const isCompatibilityBypassEndpoint = (url) => (url || '').includes('/token/');

// Request interceptor to handle authentication
api.interceptors.request.use(
  async (config) => {
    // Set baseURL dynamically for each request to support runtime API switching
    config.baseURL = getApiBaseUrl();
    
    const accessToken = localStorage.getItem('access_token');
    const refreshTokenValue = localStorage.getItem('refresh_token');
    const activeCustomer = localStorage.getItem('active_customer');
    // Will be used in the future when tenant support is added
    const activeTenant = localStorage.getItem('active_tenant');
    // Get active site for site filtering
    const activeSite = localStorage.getItem('active_site');
    
    // Don't try to refresh for token-related endpoints or if already refreshing
    const isTokenEndpoint = config.url?.includes('/token/');

    if (
      frontendCompatibilityStatus === 'incompatible' &&
      isWriteMethod(config.method) &&
      !isCompatibilityBypassEndpoint(config.url)
    ) {
      toast({
        title: 'Update required',
        description: 'This frontend version is incompatible with the backend. Update app to perform changes.',
        variant: 'destructive',
        dedupeKey: 'frontend-incompatible',
      });
      return Promise.reject(new Error('Frontend version is incompatible with backend.'));
    }
    
    // If we have a refresh token and the access token is expired (except for token endpoints)
    if (!isTokenEndpoint && refreshTokenValue && accessToken && isTokenExpired() && !isRefreshing) {
      console.log('Token expired, attempting refresh before request to:', config.url);
      // Prevent multiple simultaneous refresh attempts
      isRefreshing = true;
      
      try {
        // Try to refresh the token before this request
        const refreshResult = await refreshToken(refreshTokenValue);
        
        // Note: Tokens are already saved to localStorage in the refreshToken function
        
        // Use the new token for this request
        config.headers.Authorization = `Bearer ${refreshResult.access}`;
        isRefreshing = false;
        
        // token-refreshed is dispatched by refreshToken() in auth.ts (single source)
        
        // Reset failedQueue since we have a new token
        processQueue(null, refreshResult.access);
      } catch (error) {
        console.error('Token refresh failed during request:', error);
        isRefreshing = false;
        processQueue(error, null);

        // Only clear storage when refresh failed due to invalid/expired token (401). Backend returns 401 for that.
        // Do not clear on 400 (e.g. validation) or 5xx/network so user can retry.
        if (axios.isAxiosError(error) && error.response && error.response.status === 401) {
          toast({
            title: 'Session expired',
            description: 'Please sign in again.',
            variant: 'destructive',
            dedupeKey: 'session-expired',
          });
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('token_expiry');
          localStorage.removeItem('user');
          localStorage.removeItem('customers');
          localStorage.removeItem('active_customer');
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }
        // On 400/5xx/network: reject so the original request fails; user stays logged in and can retry
      }
    } else if (accessToken && !isTokenEndpoint) {
      // Add Authorization header to all requests except auth endpoints
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    
    // Add X-Customer-ID header for all requests except auth-related ones
    if (activeCustomer && !isTokenEndpoint) {
      const customer = JSON.parse(activeCustomer);
      config.headers['X-Customer-ID'] = customer.id;
    }
    
    // Add X-Site-ID header for site filtering
    if (activeSite && !isTokenEndpoint) {
      try {
        const site = JSON.parse(activeSite);
        if (site && site.id) {
          config.headers['X-Site-ID'] = String(site.id);
        }
      } catch (error) {
        // Silently handle parse errors - site will just not be set
      }
    }
    
    // Add X-Tenant-ID header when tenant support is implemented
    if (activeTenant && !isTokenEndpoint) {
      try {
        const tenant = JSON.parse(activeTenant);
        config.headers['X-Tenant-ID'] = tenant.id;
      } catch (error) {
        console.warn('Failed to parse active tenant', error);
      }
    }

    // Always report the running frontend version to backend when available.
    if (typeof __APP_VERSION__ !== 'undefined' && __APP_VERSION__) {
      config.headers['X-Client-Version'] = __APP_VERSION__;
    }
    
    return config;
  },
  (error) => {
    captureException(error, { type: 'api_request_error', url: error?.config?.url });
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle Error 524 (Origin Timeout)
    if (error.response?.status === 524) {
      toast({
        title: "Server Error",
        description: "The request timed out. Please try again.",
        variant: "destructive",
        dedupeKey: "request-timeout",
      });
      return Promise.reject(new Error("Request timed out"));
    }

    // Add debugging for 401 responses
    if (error.response?.status === 401) {
      console.log('Received 401 on:', originalRequest?.url, 'Retry attempt:', originalRequest?._retry);
      
      // Check specifically for token_not_valid error code
      const errorData = error.response?.data;
      
      // Check for any invalid token in refresh request
      const isTokenEndpoint = originalRequest?.url?.includes('/token/');
      const isInvalidToken = errorData?.code === 'token_not_valid';
      
      // If this is a refresh token request that failed with token_not_valid, stop retrying
      if (isTokenEndpoint && error.response?.status === 401) {
        console.error('Refresh token is invalid or expired. Error detail:', errorData?.detail ?? errorData);
        toast({
          title: 'Session expired',
          description: 'Please sign in again.',
          variant: 'destructive',
          dedupeKey: 'session-expired',
        });
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('token_expiry');
        localStorage.removeItem('user');
        localStorage.removeItem('customers');
        localStorage.removeItem('active_customer');
        
        // Clear any in-progress refresh state
        isRefreshing = false;
        processQueue(error, null);
        
        // Import and clear the React Query cache
        import('../queryClient').then(({ queryClient }) => {
          // Clear all queries from cache
          queryClient.clear();
          console.log('React Query cache cleared due to invalid token');
          
          // Redirect to login page
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
        });
        
        return Promise.reject(error);
      }
      
      if (errorData?.code === 'token_not_valid') {
        console.log('Detected token_not_valid error, will attempt refresh');
      }
    }
    
    // Special handling for login attempts - don't redirect or refresh on login failures
    if (isLoginAttempt(originalRequest)) {
      return Promise.reject(error);
    }
    
    // If error is unauthorized and we haven't already tried to refresh
    // OR if we have a specific token_not_valid error
    const errorData = error.response?.data;
    const isTokenNotValidError = errorData?.code === 'token_not_valid' || 
                               errorData?.detail === 'Given token not valid for any token type';
    
    if ((error.response?.status === 401 || isTokenNotValidError) && !originalRequest._retry) {
      // Mark this request as having attempted a retry
      originalRequest._retry = true;
      
      // If we're already refreshing, queue this request
      if (isRefreshing) {
        console.log('Token already refreshing, queueing request to:', originalRequest?.url);
        return new Promise(function(resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      isRefreshing = true;
      console.log('Attempting to refresh token for 401/token_not_valid response on:', originalRequest?.url);

      try {
        const refreshTokenValue = localStorage.getItem('refresh_token');
        if (!refreshTokenValue) {
          console.error('No refresh token available, redirecting to login');
          // No refresh token, clear storage and redirect to login
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('token_expiry');
          localStorage.removeItem('user');
          localStorage.removeItem('customers');
          localStorage.removeItem('active_customer');
          
          // Only redirect if not already on login page
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }
        
        // Try to get new tokens
        const response = await refreshToken(refreshTokenValue);
        
        // Note: Tokens are already saved to localStorage in the refreshToken function
        
        // Update the original request authorization header
        originalRequest.headers['Authorization'] = `Bearer ${response.access}`;
        
        // token-refreshed is dispatched by refreshToken() in auth.ts (single source)
        
        // Process any queued requests with the new token
        processQueue(null, response.access);
        
        isRefreshing = false;
        console.log('Token refresh successful, retrying request to:', originalRequest?.url);
        return api(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh failed during 401/token_not_valid handling:', refreshError);
        processQueue(refreshError, null);
        isRefreshing = false;

        // Only clear tokens and logout when refresh failed due to invalid/expired token (401). Backend returns 401 for that.
        // Do not clear on 400/5xx or network errors so the user stays logged in and can retry.
        const isAuthError = axios.isAxiosError(refreshError) && refreshError.response && (
          refreshError.response.status === 401 ||
          refreshError.response?.data?.code === 'token_not_valid'
        );
        if (!isAuthError) {
          console.warn('Token refresh failed (server/network error), keeping session. User can retry.');
          return Promise.reject(refreshError);
        }

        toast({
          title: 'Session expired',
          description: 'Please sign in again.',
          variant: 'destructive',
          dedupeKey: 'session-expired',
        });
        console.error('Token refresh failed due to invalid/expired token - logging out');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        localStorage.removeItem('customers');
        localStorage.removeItem('active_customer');
        localStorage.removeItem('token_expiry');

        import('../queryClient').then(({ queryClient }) => {
          queryClient.clear();
        });

        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    } else if ((error.response?.status === 401 || isTokenNotValidError) && originalRequest._retry) {
      // We already refreshed and retried; 401 on the retry means "no access to this resource"
      // (e.g. missing site, no permission), not "session invalid". Do not logout.
      toast({
        title: "Access restricted",
        description: "You don't have access to this resource. Try selecting a site or contact your admin.",
        variant: "destructive",
        dedupeKey: "access-restricted",
      });
      return Promise.reject(error);
    } else if (error.response?.status >= 500) {
      if (originalRequest?.skipErrorToast) {
        return Promise.reject(error);
      }
      // Report server errors to Sentry
      captureException(error, { 
        type: 'api_server_error',
        url: originalRequest?.url,
        status: error.response?.status,
        data: error.response?.data
      });
      
      // Handle 502 (Bad Gateway) differently - these are usually temporary
      if (error.response?.status === 502) {
        // Don't show toast for every 502 - let the health system handle it
        // Just log it for debugging
        console.warn('502 Bad Gateway error on:', originalRequest?.url);
        
        // Only show toast if this is a critical request (not just background health checks)
        const isCriticalRequest = originalRequest?.url?.includes('/token/') || 
                                 originalRequest?.url?.includes('/users/') ||
                                 originalRequest?.url?.includes('/tenants/') ||
                                 originalRequest?.url?.includes('/customers/');
        
        if (isCriticalRequest) {
          toast({
            title: "Service temporarily unavailable",
            description: "The server is temporarily down. Please try again in a moment.",
            variant: "destructive",
            dedupeKey: "service-unavailable",
          });
        }
      } else {
        // Show toast for other server errors
        toast({
          title: "Server error",
          description: "There was a problem with the server. Please try again later.",
          variant: "destructive",
          dedupeKey: "server-error",
        });
      }
    }
    
    return Promise.reject(error);
  }
);

// Add a function to get timezones
export const getTimezones = async (): Promise<string[]> => {
  try {
    const response = await api.get('/core/timezones/');
    return response.data.data || [];
  } catch (error) {
    console.error('Error fetching timezones:', error);
    return [];
  }
};

// Search API function
export const searchApi = async (
  query?: string,
  types?: string[],
  siteId?: number | string,
  signal?: AbortSignal
): Promise<import('./types').SearchResultsResponse> => {
  try {
    const params: Record<string, string | string[]> = {};
    
    // Only add query if provided and not empty
    // When filtering by type, query is optional
    if (query && query.trim() && query.trim() !== '*') {
      params.query = query.trim();
    }
    
    // Add types if provided (axios will handle array as multiple query params)
    if (types && types.length > 0) {
      params.types = types;
    }
    
    // Add site_id if provided
    if (siteId) {
      params.site_id = String(siteId);
    }
    
    const response = await api.get('/search/', { params, signal });
    return response.data;
  } catch (error) {
    console.error('Error searching:', error);
    throw error;
  }
};

// Get available search types
export const getSearchTypes = async (
  signal?: AbortSignal
): Promise<import('./types').SearchTypesResponse> => {
  try {
    const response = await api.get('/search/types/', { signal });
    return response.data;
  } catch (error) {
    // Don't log abort errors - they're expected when React Query cancels requests
    if (axios.isCancel(error) || (axios.isAxiosError(error) && error.code === 'ERR_CANCELED')) {
      // Silently handle canceled requests - this is expected behavior
      throw error;
    }
    console.error('Error fetching search types:', error);
    throw error;
  }
};

// Get task status (Core API)
export interface TaskStatusResponse {
  task_id?: string;
  status: string; // "PENDING", "STARTED", "SUCCESS", "FAILURE", "REVOKED", etc.
  ready?: boolean;
  successful?: boolean;
  failed?: boolean;
  result?: unknown;
  error?: string | null;
  traceback?: string | null;
  message?: string;
}

export const getTaskStatus = async (
  taskId: string,
  signal?: AbortSignal
): Promise<TaskStatusResponse> => {
  try {
    const response = await api.get<TaskStatusResponse>(`/tasks/${taskId}/`, { signal });
    return response.data;
  } catch (error) {
    console.error('Error fetching task status:', error);
    throw error;
  }
};

export const getCoreVersionInfo = async (
  signal?: AbortSignal
): Promise<CoreVersionInfoResponse> => {
  try {
    const response = await api.get<CoreVersionInfoResponse>('/core/version/', { signal });
    return response.data;
  } catch (error) {
    // Version info is non-critical (used for display/soft-gating). Avoid noisy console errors in smoke tests.
    return {
      backend_version: 'unknown',
      frontend_version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : null,
      minimum_frontend_version: null,
      compatibility_status: 'unknown',
      reason: 'Version endpoint unavailable',
    };
  }
};

export default api;
